/* eslint-disable @typescript-eslint/no-explicit-any */
import { useQuery } from '@tanstack/react-query';
import {
	checkArrayContainsString,
	convertProduction,
	parseFcApplicable,
} from '@utils/functions';
import { formatDate } from '@utils/functions';
import { getProductions } from '@features/sourcing/productions/api/get-productions';
import { NOT_APPLICABLE } from '@config/constants/common';
import {
	numericConversionFactor,
	numericTokenEquivalent,
} from '@utils/formats';
import {
	getConvertionFactor,
	parseTokenEquivalentToTCO2E,
} from '@utils/productions';

export const useProductions = (
	page: number,
	size: number,
	queryParams: any,
	sortParams: any
) => {
	const { data, isFetching, isFetched } = useQuery({
		queryKey: [
			'productions-start-mint',
			{
				page: page,
				size: size,
				queryParams: queryParams,
				sortParams: sortParams,
			},
		],
		queryFn: () =>
			getProductions({
				page: page,
				pageSize: size,
				queryParams: queryParams,
				sortParams: sortParams,
			}),
		enabled: checkArrayContainsString(queryParams),
		staleTime: 100,
	});

	const updateList = (productions: any) =>
		productions.reduce((acc: any, currentItem: any) => {
			// Fc applicable
			const fcApplicable = parseFcApplicable(
				currentItem.conversionFactorOverride,
				currentItem.conversionFactor,
				currentItem.unit
			);

			const convertionFactor = getConvertionFactor(
				currentItem.conversionFactorOverride,
				currentItem.conversionFactor
			);

			// Mint status
			const { status } = currentItem;

			const mintStatus = status ?? 'None';
			const modifiedItem = {
				...currentItem,
				site: currentItem.site
					? currentItem.site.name
					: NOT_APPLICABLE,
				projectId: currentItem.project
					? currentItem.project.id
					: NOT_APPLICABLE,
				project: currentItem.project
					? currentItem.project.name
					: NOT_APPLICABLE,
				startDate: formatDate(
					currentItem.startDate,
					'DD/MM/YYYY'
				),
				endDate: formatDate(
					currentItem.endDate,
					'DD/MM/YYYY'
				),
				production: convertProduction(
					currentItem.quantity,
					currentItem.unit
				),
				tokenEquivalent: parseTokenEquivalentToTCO2E(
					convertionFactor,
					currentItem.quantity
				),
				numericTokenEquivalent: numericTokenEquivalent(
					numericConversionFactor(
						currentItem.conversionFactorOverride,
						currentItem.conversionFactor
					),
					currentItem.quantity
				),
				fcApplicable: fcApplicable,
				mintStatus: mintStatus,
			};
			acc.push(modifiedItem);

			return acc;
		}, []);

	return {
		data: data ? updateList(data?.rows) : [],
		isLoading: isFetching && !isFetched,
		isFetched,
		total: data?.total,
		page: data?.page,
		pageSize: data?.pageSize,
		totalPages: data?.totalPages,
	};
};
